import { useState, useRef, useEffect, useCallback } from "react";
import { Album } from "./useTracks";
import { pb } from "@/lib/playbackTelemetry";
import { acquire, release, warmPool, getSignal } from "@/lib/audioPool";

export interface QueueTrack {
  id: string;
  title: string;
  author: string;
  dedicateTo: string;
  excerpt: string;
  language: string;
  duration: string;
  src: string;
  albumId: number;
  isIntro: boolean;
  trackGroupId: string | null;
  /** ISO upload timestamp — used to prioritise newest tracks in Smart Shuffle */
  createdAt?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Played History — persists ALL played track IDs for full-cycle enforcement.
// History is only cleared when every track in the library has been played,
// implementing a true "broadcast rotation" where every track gets its turn.
// ─────────────────────────────────────────────────────────────────────────────

const HISTORY_KEY = "llf_played_history";
const GROUP_HISTORY_KEY = "llf_played_groups";

function readHistory(): string[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeHistory(history: string[]): void {
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(history));
  } catch {
    // localStorage unavailable — silently ignore
  }
}

function syncHistoryToLibrary(history: string[], tracks: QueueTrack[]): string[] {
  const ids = new Set(tracks.map((t) => t.id));
  const seen = new Set<string>();
  const valid: string[] = [];

  for (const id of history) {
    if (!ids.has(id) || seen.has(id)) continue;
    seen.add(id);
    valid.push(id);
  }

  if (valid.length !== history.length || valid.some((id, i) => id !== history[i])) {
    writeHistory(valid);
  }

  return valid;
}

function readGroupHistory(): string[] {
  try {
    const raw = localStorage.getItem(GROUP_HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * Mark a track as played. Only the actually-played track is recorded so
 * Smart Shuffle rotates through EVERY track in the library before repeating.
 * (Previously sibling tracks in the same fuzzy/implicit group were also
 * marked played, which caused many tracks to be silently skipped each cycle.)
 */
function appendHistory(id: string, _allTracks?: QueueTrack[]): void {
  try {
    const history = readHistory();
    if (!history.includes(id)) history.push(id);
    writeHistory(history);
  } catch {
    // localStorage unavailable — silently ignore
  }
}

function clearHistory(): void {
  try {
    localStorage.removeItem(HISTORY_KEY);
    localStorage.removeItem(GROUP_HISTORY_KEY);
  } catch { /* ignore */ }
}

// ─────────────────────────────────────────────────────────────────────────────
// Implicit Track Grouping — fuzzy title/artist matching for remix detection
// ─────────────────────────────────────────────────────────────────────────────

const GROUP_CACHE_KEY = "llf_implicit_groups";

/** Common modifiers stripped before comparison */
const STRIP_MODIFIERS =
  /\b(remix|mix|edit|version|vip|live|radio\s*edit|original|instrumental|acoustic|remaster|remastered|pt\.?\s*\d+|part\s*\d+|dub)\b/gi;
/** Bracket/parenthesis content */
const STRIP_BRACKETS = /[\(\[\{][^\)\]\}]*[\)\]\}]/g;
/** Track number prefix e.g. "Track 1   " */
const STRIP_TRACK_NUM = /^track\s*\d+\s*/i;

/** Normalise a title for comparison */
function normaliseTitle(raw: string): string {
  return raw
    .replace(STRIP_BRACKETS, "")
    .replace(STRIP_MODIFIERS, "")
    .replace(STRIP_TRACK_NUM, "")
    .replace(/[^\p{L}\p{N}\s]/gu, "")   // strip punctuation
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

/** Normalise author for comparison */
function normaliseAuthor(raw: string): string {
  return raw
    .replace(/[^\p{L}\p{N}\s]/gu, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

/** Parse duration string "M:SS" or "H:MM:SS" to seconds */
function durationToSeconds(d: string): number {
  if (!d) return 0;
  const parts = d.split(":").map(Number);
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  return 0;
}

/** Levenshtein distance */
function levenshtein(a: string, b: string): number {
  const m = a.length, n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  const dp: number[] = Array.from({ length: n + 1 }, (_, i) => i);
  for (let i = 1; i <= m; i++) {
    let prev = i;
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      const val = Math.min(dp[j] + 1, prev + 1, dp[j - 1] + cost);
      dp[j - 1] = prev;
      prev = val;
    }
    dp[n] = prev;
  }
  return dp[n];
}

/** Similarity 0–1 between two normalised strings */
function stringSimilarity(a: string, b: string): number {
  if (a === b) return 1;
  if (!a || !b) return 0;
  const maxLen = Math.max(a.length, b.length);
  if (maxLen === 0) return 1;
  return 1 - levenshtein(a, b) / maxLen;
}

/** Compute similarity score between two tracks (0–1) */
function trackSimilarity(a: QueueTrack, b: QueueTrack): number {
  const titleA = normaliseTitle(a.title);
  const titleB = normaliseTitle(b.title);
  const authorA = normaliseAuthor(a.author);
  const authorB = normaliseAuthor(b.author);

  // Both must have reasonable title content after normalisation
  if (!titleA || !titleB) return 0;

  const titleSim = stringSimilarity(titleA, titleB);
  const authorSim = stringSimilarity(authorA, authorB);

  // Duration proximity (within 30s = 1.0, linear falloff to 0 at 120s)
  const durA = durationToSeconds(a.duration);
  const durB = durationToSeconds(b.duration);
  const durDiff = Math.abs(durA - durB);
  const durSim = durA > 0 && durB > 0 ? Math.max(0, 1 - durDiff / 120) : 0.5;

  // Weighted composite: title 60%, author 25%, duration 15%
  const score = titleSim * 0.60 + authorSim * 0.25 + durSim * 0.15;
  return score;
}

/** Threshold above which two tracks are considered variants */
const SIMILARITY_THRESHOLD = 0.72;

/**
 * Compute implicit groups for all tracks. Uses union-find to cluster.
 * Returns a Map of trackId → groupKey.
 * Respects explicit track_group_id when set; otherwise uses fuzzy matching.
 * Results are cached in localStorage to avoid recomputation.
 */
function computeImplicitGroups(tracks: QueueTrack[]): Map<string, string> {
  // Check cache first
  try {
    const cached = localStorage.getItem(GROUP_CACHE_KEY);
    if (cached) {
      const { version, groups } = JSON.parse(cached);
      const currentVersion = tracks.map((t) => t.id).sort().join(",");
      if (version === currentVersion && groups) {
        return new Map(Object.entries(groups as Record<string, string>));
      }
    }
  } catch { /* cache miss */ }

  // Union-Find
  const parent = new Map<string, string>();
  const find = (x: string): string => {
    while (parent.get(x) !== x) {
      parent.set(x, parent.get(parent.get(x)!)!); // path compression
      x = parent.get(x)!;
    }
    return x;
  };
  const union = (a: string, b: string) => {
    const ra = find(a), rb = find(b);
    if (ra !== rb) parent.set(rb, ra);
  };

  // Initialise
  for (const t of tracks) parent.set(t.id, t.id);

  // Step 1: Union tracks with explicit track_group_id
  const explicitGroups = new Map<string, string[]>();
  for (const t of tracks) {
    if (t.trackGroupId) {
      if (!explicitGroups.has(t.trackGroupId)) explicitGroups.set(t.trackGroupId, []);
      explicitGroups.get(t.trackGroupId)!.push(t.id);
    }
  }
  for (const ids of explicitGroups.values()) {
    for (let i = 1; i < ids.length; i++) union(ids[0], ids[i]);
  }

  // Step 2: Fuzzy matching for non-intro, non-explicit tracks
  const candidates = tracks.filter((t) => !t.isIntro);
  for (let i = 0; i < candidates.length; i++) {
    for (let j = i + 1; j < candidates.length; j++) {
      const a = candidates[i], b = candidates[j];
      // Skip if both already have explicit groups and they differ
      if (a.trackGroupId && b.trackGroupId && a.trackGroupId !== b.trackGroupId) continue;
      const sim = trackSimilarity(a, b);
      if (sim >= SIMILARITY_THRESHOLD) {
        union(a.id, b.id);
      }
    }
  }

  // Build result map
  const result = new Map<string, string>();
  for (const t of tracks) result.set(t.id, find(t.id));

  // Cache
  try {
    const version = tracks.map((t) => t.id).sort().join(",");
    const obj: Record<string, string> = {};
    for (const [k, v] of result) obj[k] = v;
    localStorage.setItem(GROUP_CACHE_KEY, JSON.stringify({ version, groups: obj }));
  } catch { /* ignore */ }

  return result;
}

// ─────────────────────────────────────────────────────────────────────────────
// Tasteful Shuffle Engine
// ─────────────────────────────────────────────────────────────────────────────

/** Fisher-Yates in-place shuffle */
function shuffleArr<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// ─────────────────────────────────────────────────────────────────────────────
// Emotional Pacing — infer energy / valence / tempo from track metadata
// ─────────────────────────────────────────────────────────────────────────────

interface EmotionalProfile {
  energy: number;   // 0–1
  valence: number;  // 0–1
  tempo: number;    // 0–1 normalised
}

/** Deterministic hash for a string → 0‒1 */
function hashStr(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return Math.abs(h % 1000) / 1000;
}

/**
 * Infer an emotional profile from track metadata when no audio analysis
 * is available. Uses title length, author hash, album position, and
 * language as lightweight proxies.
 */
function getEmotionalProfile(track: QueueTrack): EmotionalProfile {
  const titleLen = track.title.length;
  const authorHash = hashStr(track.author);
  const langHash = hashStr(track.language);

  // Energy: short titles + intro tracks → calmer; longer titles → livelier
  let energy = Math.min(1, titleLen / 40) * 0.6 + authorHash * 0.4;
  if (track.isIntro) energy = Math.max(0, energy - 0.3);

  // Valence: derived from language hash + album position
  const valence = (langHash * 0.5 + (track.albumId % 10) / 10 * 0.5);

  // Tempo: title word-count as rough proxy
  const wordCount = track.title.trim().split(/\s+/).length;
  const tempo = Math.min(1, wordCount / 8) * 0.7 + authorHash * 0.3;

  return {
    energy: Math.max(0, Math.min(1, energy)),
    valence: Math.max(0, Math.min(1, valence)),
    tempo: Math.max(0, Math.min(1, tempo)),
  };
}

/** Emotional distance between two tracks (0 = identical feel, 1 = max contrast) */
function emotionalDistance(a: QueueTrack, b: QueueTrack): number {
  const pa = getEmotionalProfile(a);
  const pb = getEmotionalProfile(b);
  const eDiff = Math.abs(pa.energy - pb.energy);
  const vDiff = Math.abs(pa.valence - pb.valence);
  const tDiff = Math.abs(pa.tempo - pb.tempo);
  return eDiff * 0.5 + vDiff * 0.3 + tDiff * 0.2;
}

/**
 * Re-order a batch so consecutive tracks sit in the emotional "sweet spot"
 * (distance 0.15–0.55). Tracks that are too similar feel repetitive; tracks
 * that are too different cause jarring whiplash. The algorithm scores each
 * candidate with a bell-curve-like penalty centred on 0.35 distance, plus a
 * small randomness component to keep things fresh.
 *
 * Applied AFTER anti-clustering. Preserves the same tracks, only changes order.
 */
const PACING_SWEET_LOW = 0.15;   // minimum desirable distance
const PACING_SWEET_MID = 0.35;   // ideal distance
const PACING_SWEET_HIGH = 0.55;  // maximum desirable distance
const PACING_MAX_ALLOWED = 0.70; // hard ceiling — never exceed unless no choice

function applyEmotionalPacing(batch: QueueTrack[]): QueueTrack[] {
  if (batch.length <= 2) return batch;

  const paced: QueueTrack[] = [];
  const remaining = [...batch];

  // Seed with the first track (preserves newest-first lead)
  let current = remaining.shift()!;
  paced.push(current);

  while (remaining.length > 0) {
    let bestIdx = 0;
    let bestScore = -Infinity;

    for (let i = 0; i < remaining.length; i++) {
      const dist = emotionalDistance(current, remaining[i]);

      // Bell-curve score: peaks at PACING_SWEET_MID, falls off symmetrically
      const deviation = Math.abs(dist - PACING_SWEET_MID);
      const halfWidth = (PACING_SWEET_HIGH - PACING_SWEET_LOW) / 2;
      const bellScore = Math.max(0, 1 - (deviation / halfWidth) ** 2);

      // Hard penalty for exceeding the ceiling (only softened on last resort)
      const ceilingPenalty = dist > PACING_MAX_ALLOWED ? -0.5 : 0;

      // Small penalty for being *too* close (monotonous run)
      const floorPenalty = dist < PACING_SWEET_LOW ? -0.2 : 0;

      // 80% pacing quality + 20% controlled randomness
      const score = (bellScore + ceilingPenalty + floorPenalty) * 0.8 + Math.random() * 0.2;

      if (score > bestScore) { bestScore = score; bestIdx = i; }
    }

    current = remaining.splice(bestIdx, 1)[0];
    paced.push(current);
  }

  return paced;
}

/** Legacy energy bucket used by anti-clustering (kept for compatibility) */
function energyBucket(track: QueueTrack): "high" | "low" {
  return getEmotionalProfile(track).energy >= 0.5 ? "high" : "low";
}

/**
 * Smart Shuffle — full-rotation queue builder.
 *
 * Behaviour:
 *  - Includes EVERY unplayed track (not just one per album) so the rotation
 *    is guaranteed to touch every track in the library before repeating.
 *  - Within each album, tracks are ordered newest-uploaded first
 *    (by `createdAt` descending) so fresh uploads surface earliest.
 *  - Albums are interleaved round-robin (with album order reshuffled per
 *    cycle) to avoid back-to-back tracks from the same album.
 *  - When `lastAlbumId` matches the head of the new cycle, we swap it with
 *    the next track so the listener doesn't get two same-album tracks back
 *    to back across the cycle boundary.
 *  - The caller (advanceToNext) handles cycle exhaustion: when no unplayed
 *    tracks remain it clears history and rebuilds a fresh full cycle.
 *
 * Note: `recentHistory` is accepted for backwards-compat with existing call
 * sites but is no longer used — exclusion is driven entirely by `excludeIds`.
 */
function buildShuffleQueue(
  tracks: QueueTrack[],
  _recentHistory: string[] = [],
  excludeIds?: Set<string>,
  lastAlbumId?: number,
): QueueTrack[] {
  const available = excludeIds && excludeIds.size > 0
    ? tracks.filter((t) => !excludeIds.has(t.id))
    : tracks;

  if (available.length === 0) return [];

  // Group by album, newest-uploaded first within each album
  const albumMap = new Map<number, QueueTrack[]>();
  for (const t of available) {
    if (!albumMap.has(t.albumId)) albumMap.set(t.albumId, []);
    albumMap.get(t.albumId)!.push(t);
  }
  for (const list of albumMap.values()) {
    list.sort((a, b) => (b.createdAt ?? "").localeCompare(a.createdAt ?? ""));
  }

  // Reshuffle album order for this cycle
  const albumQueues = shuffleArr(Array.from(albumMap.values()));

  // Round-robin interleave so albums don't cluster back-to-back
  const order: QueueTrack[] = [];
  let added = true;
  while (added) {
    added = false;
    for (const list of albumQueues) {
      const next = list.shift();
      if (next) {
        order.push(next);
        added = true;
      }
    }
  }

  // Avoid same-album back-to-back across cycle boundary
  if (lastAlbumId != null && order.length > 1 && order[0].albumId === lastAlbumId) {
    for (let i = 1; i < order.length; i++) {
      if (order[i].albumId !== lastAlbumId) {
        [order[0], order[i]] = [order[i], order[0]];
        break;
      }
    }
  }

  return order;
}

function formatTime(seconds: number): string {
  if (!isFinite(seconds) || isNaN(seconds)) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Bluetooth / iOS car-audio compatibility mode
// ─────────────────────────────────────────────────────────────────────────────
// Car Bluetooth sinks (especially iOS → A2DP/AVRCP via head units like VW MIB)
// have three quirks the standard playback path doesn't handle well:
//
//   1. `pause()` lags the codec by 100–300 ms, so a hard stop + immediate start
//      causes audible overlap with the incoming track.
//   2. Track-to-track codec handoff has its own ~200 ms gap, so a 1.5 s
//      crossfade is too short to mask it — listeners hear a dropout.
//   3. The BT pipeline buffers more than headphones, so preload needs to start
//      earlier or the next track stutters when the crossfade begins.
//
// Compat mode is auto-enabled on iOS (where these quirks are most pronounced)
// and can be forced on/off via `localStorage.llf_bt_compat = "1" | "0"`.
// ─────────────────────────────────────────────────────────────────────────────

function detectIOS(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  // iPadOS 13+ identifies as MacIntel — disambiguate via touch points
  return navigator.platform === "MacIntel" && (navigator as unknown as { maxTouchPoints?: number }).maxTouchPoints! > 1;
}

function detectCompatMode(): boolean {
  try {
    const stored = localStorage.getItem("llf_bt_compat");
    if (stored === "1") return true;
    if (stored === "0") return false;
  } catch { /* ignore */ }
  return detectIOS();
}

const BT_COMPAT = detectCompatMode();

/** Duration of the crossfade between tracks (seconds).
 *  Compat mode uses a longer fade to mask BT codec handoff (~200 ms gap). */
const CROSSFADE_DURATION = BT_COMPAT ? 2.6 : 1.5;
/** How many seconds before end to start preloading.
 *  Compat mode preloads earlier so the BT pipeline has buffer headroom
 *  before the crossfade actually begins. */
const PRELOAD_AHEAD_SECONDS = BT_COMPAT ? 12 : 8;
/** After silenceAll(), the outgoing audio's pause() may take 100–300 ms to
 *  reach the BT codec. Ramping the incoming track up from 0 over this window
 *  avoids the audible overlap window without sacrificing the user-gesture
 *  guarantee (we still call play() synchronously inside the gesture). */
const COMPAT_FADE_IN_MS = BT_COMPAT ? 140 : 0;

/** Smooth volume ramp from 0 → 1 over `durationMs`. Use after starting an
 *  audio element inside a user-gesture frame when BT compat mode is active. */
function rampVolumeIn(audio: HTMLMediaElement, durationMs: number): void {
  if (durationMs <= 0) { try { audio.volume = 1; } catch { /* ignore */ } return; }
  try { audio.volume = 0; } catch { /* ignore */ return; }
  const start = performance.now();
  const step = () => {
    const t = Math.min((performance.now() - start) / durationMs, 1);
    try { audio.volume = t; } catch { return; }
    if (t < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

/**
 * Acquire a media element from the pool for a given source URL.
 * `.mp4` files use a `<video>` element; everything else uses `<audio>`.
 * The pool recycles elements to avoid cold-start rebuffering on rapid skips.
 */
function createMediaElement(src: string): HTMLAudioElement {
  return acquire(src);
}

/** Release a media element back to the pool for reuse */
function cleanupMediaElement(el: HTMLMediaElement) {
  release(el);
}

/**
 * Unlock the Web Audio context with a silent muted play/pause cycle.
 * Returns a promise that resolves once the unlock completes (or silently fails).
 * This satisfies iOS Safari's requirement for audio to be triggered inside a
 * user-gesture before any real playback can start.
 */
async function silentUnlock(): Promise<void> {
  try {
    // Warm the audio pool on first user gesture so elements are ready
    warmPool();
    const a = new Audio();
    a.muted = true;
    // data-URI of a ~0.1s silent MP3 so we don't need a network fetch
    a.src =
      "data:audio/mpeg;base64,SUQzBAAAAAABEVRYWFgAAAAtAAADY29tbWVudABCaWdTb3VuZCBUZWNobm9sb2d5IEluYy4gLy8gc291bmRiaWJsZS5jb20AVEVOQQAAAAAMAAADMQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAP/zUEAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAABJbmZvAAAADwAAAAEAAAMgAP//////////////////AAAATGF2YzU5LjE4LjEwMAAAAAAAAAAAAAAkCQAAAAAAAAMgAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAP/zUMAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAABJbmZvAAAADwAAAAEAAAGQAP//////////////////AAAATGF2YzU5LjE4LjEwMAAAAAAAAAAAAAAkCgAAAAAAAAGQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA";
    await a.play();
    a.pause();
    a.src = "";
  } catch {
    // Ignore — silent unlock failure is non-fatal
  }
}

export function usePlaybackEngine(
  albums: Album[],
  getPublicUrl: (p: string) => string,
  pageReady = true
) {
  // Stable ref for getPublicUrl to avoid re-render loops
  const getPublicUrlRef = useRef(getPublicUrl);
  useEffect(() => { getPublicUrlRef.current = getPublicUrl; });

  const flatTracks = useCallback((): QueueTrack[] => {
    const result: QueueTrack[] = [];
    for (const album of albums) {
      for (const t of album.tracks) {
        const src = getPublicUrlRef.current(t.storage_path);
        // Skip tracks with empty/invalid src to prevent MEDIA_ELEMENT_ERROR cascades
        if (!src || src === window.location.origin + "/" || src.endsWith("/object/public/")) continue;
        result.push({
          id: t.id,
          title: t.title,
          author: t.author,
          dedicateTo: t.dedicate_to,
          excerpt: t.excerpt,
          language: t.language,
          duration: t.duration,
          src,
          albumId: t.album_id,
          isIntro: t.is_intro,
          trackGroupId: t.track_group_id ?? null,
          createdAt: t.created_at,
        });
      }
    }
    return result;
  }, [albums]);

  // ── Audio elements ──────────────────────────────────────────────────────────
  const audioRef = useRef<HTMLAudioElement | null>(null);
  // Preloaded next track: { id, audio }
  const preloadRef = useRef<{ id: string; audio: HTMLAudioElement } | null>(null);
  // The "incoming" audio during an active crossfade
  const crossfadeNextRef = useRef<HTMLAudioElement | null>(null);
  // rAF handle for the crossfade animation
  const rAFRef = useRef<number | null>(null);
  const crossfadingRef = useRef(false);
  const crossfadeHandoffRef = useRef(false);
  // Whether preload has been triggered for the next track in this session
  const preloadedForIdRef = useRef<string | null>(null);
  // When playTrack is called for a NEW id, we pre-create the audio element
  // INSIDE the user-gesture callback so .play() is allowed by the browser.
  const pendingAudioRef = useRef<{ id: string; audio: HTMLAudioElement } | null>(null);

  // ── Autoplay-pending state ───────────────────────────────────────────────────
  const autoplayPendingRef = useRef(false);
  const userPausedRef = useRef(false);
  const interactionListenerAttachedRef = useRef(false);
  // Prevents rapid-fire skips from racing (skip lock)
  const skipLockRef = useRef(false);
  // Stall recovery state — lifted to refs so pauseAll/playTrack can cancel
  const stallTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stallRetryRef = useRef(0);

  const clearStallState = useCallback(() => {
    if (stallTimerRef.current) { clearTimeout(stallTimerRef.current); stallTimerRef.current = null; }
    stallRetryRef.current = 0;
  }, []);

  /**
   * Immediately silence every active audio source (current, pending, crossfade,
   * preload). Called before any track switch so rapid skips never overlap.
   */
  const silenceAll = useCallback(() => {
    const hadCrossfade = crossfadingRef.current;
    const hadPending = !!pendingAudioRef.current;
    const hadStallTimer = !!stallTimerRef.current;
    pb.silenceAll(currentIdRef.current, hadCrossfade, hadPending, hadStallTimer);
    // Pause the main element AND zero its volume — on iOS Bluetooth/AirPlay
    // sinks pause() is not instantaneous (~100-300ms latency to the codec).
    // Setting volume=0 in the same synchronous frame guarantees no audible
    // overlap with the incoming track.
    const cur = audioRef.current;
    if (cur) {
      try { cur.volume = 0; } catch { /* ignore */ }
      try { cur.pause(); } catch { /* ignore */ }
    }
    // Kill any in-progress crossfade
    if (rAFRef.current !== null) {
      cancelAnimationFrame(rAFRef.current);
      rAFRef.current = null;
    }
    if (crossfadeNextRef.current) {
      try { crossfadeNextRef.current.volume = 0; } catch { /* ignore */ }
      cleanupMediaElement(crossfadeNextRef.current);
      crossfadeNextRef.current = null;
    }
    crossfadingRef.current = false;
    // Kill pending audio that was pre-started inside a user gesture
    if (pendingAudioRef.current) {
      try { pendingAudioRef.current.audio.volume = 0; } catch { /* ignore */ }
      try { pendingAudioRef.current.audio.pause(); } catch { /* ignore */ }
    }
    // Defensive: ensure the preload buffer never plays (some BT stacks have
    // been observed to start a buffered-but-not-yet-played element when the
    // sink switches focus during a track change).
    if (preloadRef.current) {
      const p = preloadRef.current.audio;
      try { p.volume = 0; } catch { /* ignore */ }
      try { p.pause(); } catch { /* ignore */ }
    }
    // Cancel any stall-recovery timers so they don't resurrect the old track
    if (stallTimerRef.current) { clearTimeout(stallTimerRef.current); stallTimerRef.current = null; }
    stallRetryRef.current = 0;
  }, []);

  const [playing, setPlaying] = useState(false);
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [autoplay, setAutoplay] = useState(true);
  // Shuffle is always ON – initialise true and never allow false from session state
  const [shuffle, setShuffle] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  
  const [duration, setDuration] = useState(0);

  const queueRef = useRef<QueueTrack[]>([]);
  const [queueVersion, setQueueVersion] = useState(0);
  const hasAutostartedRef = useRef(false);

  // ── Session-wide played tracking ────────────────────────────────────────────
  // Tracks every ID played in this session so full-rotation is enforced:
  // no track repeats until ALL tracks have been played.
  // Initialised from localStorage so it survives page reloads.
  const playedSessionRef = useRef<Set<string>>(new Set(readHistory()));
  // Stable ref to flatTracks for use inside advanceToNext without re-renders
  const flatTracksRef = useRef(flatTracks);
  useEffect(() => { flatTracksRef.current = flatTracks; });

  /**
   * Advance to the next track in the queue, auto-regenerating a new cycle
   * when the current queue is exhausted. Returns the next track or null.
   */
  const advanceToNext = useCallback((): QueueTrack | null => {
    const queue = queueRef.current;
    const idx = queue.findIndex((t) => t.id === currentIdRef.current);

    // Try the next track in the existing queue
    let next = queue[idx + 1];
    if (next) {
      const current = queue[idx];
      if (current) {
        const distToNext = emotionalDistance(current, next);

        // ── Pacing / whiplash guard ──────────────────────────────────────
        // If the queued-next track exceeds the pacing ceiling, scan up to
        // LOOKAHEAD positions ahead for a smoother candidate. The best
        // candidate is the one closest to PACING_SWEET_MID (0.35 distance).
        // A 12% random chance still allows an occasional bold jump so the
        // mix doesn't feel robotic.
        const LOOKAHEAD = 3;
        if (
          distToNext > PACING_MAX_ALLOWED &&
          Math.random() >= 0.12
        ) {
          let bestAltIdx = -1;
          let bestAltScore = Infinity;
          for (let k = 2; k <= LOOKAHEAD + 1 && idx + k < queue.length; k++) {
            const cand = queue[idx + k];
            const candDist = emotionalDistance(current, cand);
            const deviation = Math.abs(candDist - PACING_SWEET_MID);
            if (deviation < bestAltScore) {
              bestAltScore = deviation;
              bestAltIdx = idx + k;
            }
          }
          if (bestAltIdx > 0 && bestAltScore < Math.abs(distToNext - PACING_SWEET_MID)) {
            const alt = queue[bestAltIdx];
            pb.whiplashSwap(current.id, next.id, alt.id, distToNext);
            // Move the chosen alt into the next slot; shift displaced tracks forward
            queueRef.current.splice(bestAltIdx, 1);
            queueRef.current.splice(idx + 1, 0, alt);
            next = alt;
          }
        }
      }
      pb.queueAdvance(currentIdRef.current, next.id, idx + 1, queue.length);
      return next;
    }

    // ── Queue exhausted — build a new cycle ──────────────────────────────────
    const all = flatTracksRef.current();
    if (all.length === 0) return null;

    // Determine the album of the last track so we avoid same-album at boundary
    const lastTrack = queue[queue.length - 1] ?? null;
    const lastAlbumId = lastTrack?.albumId;

    const validHistory = syncHistoryToLibrary(readHistory(), all);
    playedSessionRef.current = new Set(validHistory);

    // Try building a queue from unplayed tracks first
    const unplayedQueue = buildShuffleQueue(
      all,
      validHistory,
      playedSessionRef.current,
      lastAlbumId,
    );

    let newQueue: QueueTrack[];
    if (unplayedQueue.length > 0) {
      newQueue = unplayedQueue;
      pb.queueRebuild("cycle_exhausted", newQueue.length, playedSessionRef.current.size, all.length);
    } else {
      // All tracks played — full cycle complete. Reset everything.
      pb.queueRebuild("cycle_reset", 0, playedSessionRef.current.size, all.length);
      playedSessionRef.current.clear();
      clearHistory();
      newQueue = buildShuffleQueue(all, [], undefined, lastAlbumId);
    }

    if (newQueue.length === 0) return null;

    // Append new cycle to queue so history / index references stay valid
    queueRef.current = [...queue, ...newQueue];
    setQueueVersion((v) => v + 1);
    preloadedForIdRef.current = null;

    return newQueue[0];
  }, []);

  // Rebuild queue when albums change
  useEffect(() => {
    const all = flatTracks();
    // Restore session tracking from persisted history (survives reloads)
    const persisted = readHistory();
    const allIds = new Set(all.map((t) => t.id));
    // If tracks were removed, prune only those stale IDs; keep valid played IDs
    // so album edits/uploads cannot make Smart Shuffle repeat already-heard tracks.
    const validHistory = syncHistoryToLibrary(persisted, all);
    const isCycleComplete = allIds.size > 0 && validHistory.length >= allIds.size;
    if (isCycleComplete) {
      playedSessionRef.current.clear();
      clearHistory();
      queueRef.current = buildShuffleQueue(all, [], undefined, undefined);
      pb.queueRebuild("cycle_reset", queueRef.current.length, 0, all.length);
    } else {
      playedSessionRef.current = new Set(validHistory);
      queueRef.current = buildShuffleQueue(all, validHistory, playedSessionRef.current, undefined);
      pb.queueRebuild("library_changed", queueRef.current.length, validHistory.length, all.length);
    }
    setQueueVersion((v) => v + 1);
    preloadedForIdRef.current = null;

    if (!hasAutostartedRef.current && queueRef.current.length > 0 && pageReady) {
      hasAutostartedRef.current = true;
      setCurrentId(queueRef.current[0].id);
    }
  }, [albums, shuffle, flatTracks, pageReady]);

  // Fire autostart once the loading screen finishes
  useEffect(() => {
    if (!pageReady) return;
    // Pre-arm the interaction listener so ANY tap/scroll/keypress anywhere
    // on the page immediately starts playback if the autoplay attempt below
    // is blocked by browser policy (notably iOS Safari). The actual silent
    // unlock + retry happens inside that listener on the first gesture.
    autoplayPendingRef.current = true;
    attachInteractionListeners();
    if (!hasAutostartedRef.current && queueRef.current.length > 0) {
      hasAutostartedRef.current = true;
      setCurrentId(queueRef.current[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageReady]);

  const currentTrack = queueRef.current.find((t) => t.id === currentId) ?? null;

  // Stable refs for use inside audio event callbacks
  const autoplayRef = useRef(autoplay);
  useEffect(() => { autoplayRef.current = autoplay; }, [autoplay]);

  const currentIdRef = useRef(currentId);
  useEffect(() => { currentIdRef.current = currentId; }, [currentId]);

  // Stable ref for advanceToNext so event handlers always use the latest version
  const advanceToNextRef = useRef(advanceToNext);
  useEffect(() => { advanceToNextRef.current = advanceToNext; }, [advanceToNext]);

  // ── First-interaction unlock ─────────────────────────────────────────────────
  // Attaches one-time capture listeners to the document so that the very first
  // tap/click/scroll/touchstart triggers playback if it was blocked by policy.
  const attachInteractionListeners = useCallback(() => {
    if (interactionListenerAttachedRef.current) return;
    interactionListenerAttachedRef.current = true;

    const triggerPendingPlay = async () => {
      // Only fire if autoplay was blocked AND the user hasn't manually paused
      if (!autoplayPendingRef.current || userPausedRef.current) return;
      autoplayPendingRef.current = false;
      removeListeners(); // eslint-disable-line

      // Silent unlock first (critical for iOS Safari)
      await silentUnlock();

      const audio = audioRef.current;
      if (!audio) return;
      audio.play()
        .then(() => setPlaying(true))
        .catch(() => {/* still blocked — give up gracefully */});
    };

    const events = ["click", "touchstart", "keydown", "scroll"] as const;
    const removeListeners = () => {
      for (const ev of events) {
        document.removeEventListener(ev, triggerPendingPlay, { capture: true });
      }
      interactionListenerAttachedRef.current = false;
    };

    for (const ev of events) {
      document.addEventListener(ev, triggerPendingPlay, { once: true, capture: true, passive: true });
    }
  }, []);

  // ── Preload helper ───────────────────────────────────────────────────────────
  const schedulePreload = useCallback((track: QueueTrack) => {
    if (preloadRef.current?.id === track.id) return;
    if (preloadRef.current) {
      cleanupMediaElement(preloadRef.current.audio);
      preloadRef.current = null;
    }
    const audio = createMediaElement(track.src);
    audio.preload = "auto";
    audio.load();
    preloadRef.current = { id: track.id, audio };
    preloadedForIdRef.current = track.id;
  }, []);

  /** Look up the track that follows `id` in the current queue. */
  const peekNextInQueue = useCallback((id: string): QueueTrack | null => {
    const q = queueRef.current;
    const idx = q.findIndex((t) => t.id === id);
    return idx >= 0 && idx + 1 < q.length ? q[idx + 1] : null;
  }, []);

  /** Eagerly prefetch whichever track comes after `id` in the queue. */
  const prefetchAfter = useCallback(
    (id: string) => {
      const upcoming = peekNextInQueue(id);
      if (upcoming) schedulePreload(upcoming);
    },
    [peekNextInQueue, schedulePreload]
  );

  // ── Crossfade ────────────────────────────────────────────────────────────────
  function startCrossfade(currentAudio: HTMLAudioElement, nextTrack: QueueTrack) {
    const fromId = currentIdRef.current ?? "?";
    pb.crossfadeStart(fromId, nextTrack.id);

    let nextAudio: HTMLAudioElement;

    if (preloadRef.current?.id === nextTrack.id) {
      nextAudio = preloadRef.current.audio;
      preloadRef.current = null;
    } else {
      if (preloadRef.current) {
        cleanupMediaElement(preloadRef.current.audio);
        preloadRef.current = null;
      }
      nextAudio = createMediaElement(nextTrack.src);
      nextAudio.preload = "auto";
    }

    crossfadeNextRef.current = nextAudio;
    nextAudio.volume = 0;
    nextAudio.play().catch(() => {
      pb.crossfadeEnd(fromId, nextTrack.id, false);
    });

    const start = performance.now();

    function tick() {
      const t = Math.min((performance.now() - start) / 1000 / CROSSFADE_DURATION, 1);
      try {
        currentAudio.volume = 1 - t;
        nextAudio.volume = t;
      } catch (_) { /* audio may have been GC'd */ }

      if (t < 1) {
        rAFRef.current = requestAnimationFrame(tick);
      } else {
        nextAudio.volume = 1;
        rAFRef.current = null;
        crossfadeNextRef.current = null;
        cleanupMediaElement(currentAudio);
        crossfadingRef.current = false;
        crossfadeHandoffRef.current = true;

        preloadRef.current = { id: nextTrack.id, audio: nextAudio };
        setPlaying(true);
        setCurrentId(nextTrack.id);
        pb.crossfadeEnd(fromId, nextTrack.id, true);
      }
    }

    rAFRef.current = requestAnimationFrame(tick);
  }

  // ── Main playback effect ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!currentTrack) return;
    pb.trackStart(currentTrack.id);
    let firstAudioFired = false;

    crossfadeHandoffRef.current = false;

    if (rAFRef.current !== null) {
      cancelAnimationFrame(rAFRef.current);
      rAFRef.current = null;
    }
    if (crossfadingRef.current && crossfadeNextRef.current) {
      cleanupMediaElement(crossfadeNextRef.current);
      crossfadeNextRef.current = null;
    }
    crossfadingRef.current = false;

    let audio: HTMLAudioElement;

    if (pendingAudioRef.current?.id === currentTrack.id) {
      const prev = audioRef.current;
      if (prev && prev !== pendingAudioRef.current.audio) {
        cleanupMediaElement(prev);
      }
      audio = pendingAudioRef.current.audio;
      pendingAudioRef.current = null;
      if (preloadRef.current?.id === currentTrack.id) {
        cleanupMediaElement(preloadRef.current.audio);
        preloadRef.current = null;
      }
    } else if (preloadRef.current?.id === currentTrack.id) {
      audio = preloadRef.current.audio;
      preloadRef.current = null;
      if (pendingAudioRef.current) {
        cleanupMediaElement(pendingAudioRef.current.audio);
        pendingAudioRef.current = null;
      }
    } else {
      if (preloadRef.current) {
        cleanupMediaElement(preloadRef.current.audio);
        preloadRef.current = null;
      }
      if (pendingAudioRef.current) {
        cleanupMediaElement(pendingAudioRef.current.audio);
        pendingAudioRef.current = null;
      }
      const prev = audioRef.current;
      if (prev) {
        cleanupMediaElement(prev);
      }
      audio = createMediaElement(currentTrack.src);
      audio.preload = "auto";
    }

    // Don't clobber an in-progress compat-mode volume ramp from skipNext/playTrack.
    if (audio.volume >= 1 || COMPAT_FADE_IN_MS === 0) audio.volume = 1;
    audioRef.current = audio;

    setCurrentTime(isFinite(audio.currentTime) ? audio.currentTime : 0);
    setDuration(isFinite(audio.duration) && audio.duration > 0 ? audio.duration : 0);

    // ── Event handlers ───────────────────────────────────────────────────────
    // Track the next-up candidate so preload and crossfade use the SAME track
    // (prevents double advanceToNext() from skipping a track).
    let nextUpTrack: QueueTrack | null = null;

    const onTimeUpdate = () => {
      // Ignore events from a replaced element (prevents stale preload/crossfade)
      if (audio !== audioRef.current) {
        pb.staleGuard("onTimeUpdate", currentTrack.id, "element_replaced");
        return;
      }
      setCurrentTime(audio.currentTime);

      const remaining = isFinite(audio.duration) ? audio.duration - audio.currentTime : Infinity;

      // Preload the next track when nearing end
      if (
        remaining <= PRELOAD_AHEAD_SECONDS &&
        preloadedForIdRef.current !== currentTrack.id
      ) {
        nextUpTrack = advanceToNextRef.current();
        if (nextUpTrack) {
          preloadedForIdRef.current = currentTrack.id;
          schedulePreload(nextUpTrack);
        }
      }

      // Start crossfade near end — reuse the already-identified next track
      if (
        autoplayRef.current &&
        !crossfadingRef.current &&
        isFinite(audio.duration) &&
        audio.duration > CROSSFADE_DURATION * 2 &&
        remaining <= CROSSFADE_DURATION
      ) {
        // If preload already resolved the next track, reuse it; otherwise resolve now
        const next = nextUpTrack ?? advanceToNextRef.current();
        if (next) {
          crossfadingRef.current = true;
          startCrossfade(audio, next);
        }
      }
    };

    const onDurationChange = () => {
      if (isFinite(audio.duration)) setDuration(audio.duration);
    };
    const onLoadedMetadata = () => {
      if (isFinite(audio.duration)) setDuration(audio.duration);
    };

    // Skip to next track on error (fail-safe) and log details
    const onError = () => {
      // Ignore errors fired AFTER this element was replaced (e.g., user tapped
      // a different track and we cleaned up the previous element). Symptom:
      // src is empty/origin-only because cleanupMediaElement set src="".
      if (audio !== audioRef.current) { pb.staleGuard("onError", currentTrack.id, "element_replaced"); return; }
      const srcAttr = audio.src;
      if (!srcAttr || srcAttr === window.location.href || srcAttr === window.location.origin + "/") return;
      const err = audio.error;
      console.error(
        `[Playback] Failed to load track "${currentTrack?.title}" (id=${currentIdRef.current})`,
        `src=${srcAttr}`,
        err ? `MediaError code=${err.code} message=${err.message}` : "unknown error"
      );
      pb.errorSkip(currentTrack.id, err?.code ?? null, err?.message ?? null);
      if (!autoplayRef.current) return;
      if (skipLockRef.current) return; // prevent error cascade
      skipLockRef.current = true;
      const next = advanceToNextRef.current();
      if (next) {
        currentIdRef.current = next.id;
        setCurrentId(next.id);
      }
      setTimeout(() => { skipLockRef.current = false; }, 300);
    };

    // Fallback for when crossfade didn't fire (e.g., autoplay off or short track)
    const onEnded = () => {
      // Ignore ended events from a replaced element (e.g. after rapid skip)
      if (audio !== audioRef.current) { pb.staleGuard("onEnded", currentTrack.id, "element_replaced"); return; }
      if (crossfadingRef.current) { pb.staleGuard("onEnded", currentTrack.id, "crossfading"); return; }
      if (skipLockRef.current) { pb.staleGuard("onEnded", currentTrack.id, "skip_locked"); return; }
      setPlaying(false);
      if (autoplayRef.current) {
        // Reuse the already-identified next track if available
        const next = nextUpTrack ?? advanceToNextRef.current();
        if (next) setCurrentId(next.id);
      }
    };

    // ── Mobile stall recovery with exponential backoff ──────────────────────
    // When the audio element enters a waiting/stalled state we retry with
    // increasing delays: 2s → 4s → 8s → 16s (max). After each timeout we
    // attempt progressively more aggressive recovery strategies:
    //   1. Nudge currentTime (force re-buffer)
    //   2. Reload the source from scratch
    //   3. Recreate the element entirely
    // If `canplay` fires at any point, the retry chain resets.
    const MAX_STALL_RETRIES = 4;
    // Use component-level refs so external callers (pauseAll, playTrack) can cancel
    stallTimerRef.current = null;
    stallRetryRef.current = 0;
    let stallStartTime: number | null = null;
    let lastStallStrategy = "unknown";

    const clearStallTimer = () => {
      if (stallTimerRef.current) { clearTimeout(stallTimerRef.current); stallTimerRef.current = null; }
    };
    const resetStallState = () => { clearStallTimer(); stallRetryRef.current = 0; stallStartTime = null; };

    const attemptStallRecovery = () => {
      if (audio !== audioRef.current || audio.paused) { resetStallState(); return; }
      // Already playing fine — readyState ≥ 3 means HAVE_FUTURE_DATA
      if (audio.readyState >= 3) { resetStallState(); return; }

      stallRetryRef.current++;
      const attempt = stallRetryRef.current;
      const strategy = attempt <= 1 ? "nudge" : attempt <= 2 ? "reload" : "recreate";
      lastStallStrategy = strategy;

      pb.stallRecovery(currentTrack.id, attempt, strategy, false);

      if (attempt <= 1) {
        // Strategy 1: nudge currentTime to force the browser to re-request data
        const pos = audio.currentTime;
        audio.currentTime = pos;
      } else if (attempt <= 2) {
        // Strategy 2: reload the same source (clears internal buffers)
        const src = audio.src;
        const pos = audio.currentTime;
        audio.src = "";
        audio.src = src;
        audio.currentTime = pos;
        audio.play().catch(() => {});
      } else if (attempt <= MAX_STALL_RETRIES) {
        // Strategy 3: recreate the element entirely (handles iOS codec lock-ups)
        const src = currentTrack.src;
        const pos = audio.currentTime;
        cleanupMediaElement(audio);
        const fresh = createMediaElement(src);
        fresh.preload = "auto";
        fresh.currentTime = pos;
        fresh.volume = 1;
        audioRef.current = fresh;
        // Core playback listeners — use fresh element's signal
        const freshSig = { signal: getSignal(fresh) };
        fresh.addEventListener("timeupdate", onTimeUpdate, freshSig);
        fresh.addEventListener("durationchange", onDurationChange, freshSig);
        fresh.addEventListener("loadedmetadata", onLoadedMetadata, freshSig);
        fresh.addEventListener("ended", onEnded, freshSig);
        fresh.addEventListener("error", onError, freshSig);
        fresh.addEventListener("waiting", onWaiting, freshSig);
        fresh.addEventListener("stalled", onWaiting, freshSig);
        fresh.addEventListener("canplay", onCanPlay, freshSig);
        // Play/pause sync for UI state
        fresh.addEventListener("play", () => setPlaying(true), freshSig);
        fresh.addEventListener("pause", () => setPlaying(false), freshSig);
        fresh.play().then(() => setPlaying(true)).catch(() => {});
        // Update local reference so subsequent retries target the new element
        audio = fresh;
      }

      // Schedule next attempt with exponential backoff (2s, 4s, 8s, 16s)
      if (stallRetryRef.current < MAX_STALL_RETRIES) {
        const delay = Math.min(2000 * Math.pow(2, stallRetryRef.current), 16000);
        stallTimerRef.current = setTimeout(attemptStallRecovery, delay);
      }
    };

    const onWaiting = () => {
      // Ignore waiting events from a replaced element
      if (audio !== audioRef.current) { pb.staleGuard("onWaiting", currentTrack.id, "element_replaced"); return; }
      pb.dropout(currentTrack.id, "waiting", { readyState: audio.readyState, currentTime: audio.currentTime });
      if (stallStartTime === null) stallStartTime = performance.now();
      clearStallTimer();
      // First attempt after a 2s grace period
      stallTimerRef.current = setTimeout(attemptStallRecovery, 2000);
    };
    const onCanPlay = () => {
      if (stallRetryRef.current > 0) {
        const totalAttempts = stallRetryRef.current;
        const stallDurationMs = stallStartTime != null ? Math.round(performance.now() - stallStartTime) : 0;
        const winningStrategy = lastStallStrategy;
        pb.stallRecovery(currentTrack.id, totalAttempts, "recovered", true);

        // Detect smooth resume: did timeupdate fire within 2s of recovery?
        let resolvedLogged = false;
        const resumeCheck = () => {
          if (resolvedLogged) return;
          resolvedLogged = true;
          pb.stallResolved(currentTrack.id, totalAttempts, winningStrategy, stallDurationMs, true);
        };
        const resumeSig = getSignal(audio);
        audio.addEventListener("timeupdate", resumeCheck, { once: true, signal: resumeSig });
        // If no timeupdate within 2s, log as non-smooth
        setTimeout(() => {
          if (!resolvedLogged) {
            resolvedLogged = true;
            pb.stallResolved(currentTrack.id, totalAttempts, winningStrategy, stallDurationMs, false);
          }
        }, 2000);
      }
      resetStallState();
    };

    // First-audio telemetry — fires once per track load
    const onFirstPlaying = () => {
      if (!firstAudioFired) {
        firstAudioFired = true;
        pb.firstAudio(currentTrack.id);
      }
    };

    // All listeners use the pool's AbortSignal — they are automatically removed
    // when the element is released/re-acquired, preventing stale handler leaks.
    const sig = { signal: getSignal(audio) };
    audio.addEventListener("timeupdate", onTimeUpdate, sig);
    audio.addEventListener("durationchange", onDurationChange, sig);
    audio.addEventListener("loadedmetadata", onLoadedMetadata, sig);
    audio.addEventListener("ended", onEnded, sig);
    audio.addEventListener("error", onError, sig);
    audio.addEventListener("waiting", onWaiting, sig);
    audio.addEventListener("stalled", onWaiting, sig);
    audio.addEventListener("canplay", onCanPlay, sig);
    audio.addEventListener("playing", onFirstPlaying, sig);

    // Helper: record the current track in persistent + session history
    const recordPlayed = () => {
      const allForHistory = flatTracksRef.current();
      appendHistory(currentTrack.id, allForHistory);
      playedSessionRef.current.add(currentTrack.id);
      const groups = computeImplicitGroups(allForHistory);
      const myGroup = groups.get(currentTrack.id);
      if (myGroup) {
        for (const t of allForHistory) {
          if (groups.get(t.id) === myGroup) playedSessionRef.current.add(t.id);
        }
      }
    };

    if (!audio.paused) {
      setPlaying(true);
      autoplayPendingRef.current = false;
      recordPlayed();
    } else {
      audio.play()
        .then(() => {
          setPlaying(true);
          autoplayPendingRef.current = false;
          userPausedRef.current = false;
          recordPlayed();
        })
        .catch(() => {
          // Autoplay blocked — arm the first-interaction trigger
          setPlaying(false);
          if (!userPausedRef.current) {
            autoplayPendingRef.current = true;
            attachInteractionListeners();
          }
        });
    }

    // Proactively preload next track right away
    const queue = queueRef.current;
    const idx = queue.findIndex((t) => t.id === currentTrack.id);
    const nextTrack = queue[idx + 1];
    if (nextTrack && preloadedForIdRef.current !== currentTrack.id) {
      preloadedForIdRef.current = currentTrack.id;
      const tid = setTimeout(() => schedulePreload(nextTrack), 200);
      return () => {
        clearTimeout(tid);
        clearStallTimer();
        // Listeners are auto-removed by the AbortSignal when the element is
        // released — no manual removeEventListener needed.
        if (!crossfadingRef.current && !crossfadeHandoffRef.current) {
          cleanupMediaElement(audio);
        }
      };
    }

    return () => {
      clearStallTimer();
      if (!crossfadingRef.current && !crossfadeHandoffRef.current) {
        cleanupMediaElement(audio);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentId]);

  // ── Public API ───────────────────────────────────────────────────────────────

  const playTrack = useCallback(
    (id: string) => {
      warmPool(); // Ensure pool is warm on first user-initiated play
      if (currentId === id) {
        const audio = audioRef.current;
        if (!audio) return;
        if (playing) {
          audio.pause();
          setPlaying(false);
          // User manually paused — cancel any pending autoplay trigger
          userPausedRef.current = true;
          autoplayPendingRef.current = false;
        } else {
          userPausedRef.current = false;
          audio.play().then(() => setPlaying(true)).catch(() => {});
        }
      } else {
        // Silence everything immediately to prevent overlap on rapid switches
        silenceAll();
        if (pendingAudioRef.current) {
          cleanupMediaElement(pendingAudioRef.current.audio);
          pendingAudioRef.current = null;
        }
        // Look in queue first, then fall back to full track list
        // (a track may not be in the queue if it was already played in the shuffle rotation)
        let track = queueRef.current.find((t) => t.id === id);
        let injected = false;
        if (!track) {
          track = flatTracksRef.current().find((t) => t.id === id) ?? undefined;
          // Inject into queue so the main effect can find it
          if (track) {
            injected = true;
            const idx = queueRef.current.findIndex((t) => t.id === currentIdRef.current);
            queueRef.current.splice(idx + 1, 0, track);
            setQueueVersion((v) => v + 1);
          }
        }
        pb.playTrackSwitch(currentId, id, injected);
        if (track) {
          const audio = createMediaElement(track.src);
          audio.preload = "auto";
          // Compat mode: start at 0 and ramp in to mask BT codec overlap window.
          rampVolumeIn(audio, COMPAT_FADE_IN_MS);
          audio.play()
            .then(() => { setPlaying(true); userPausedRef.current = false; })
            .catch(() => setPlaying(false));
          pendingAudioRef.current = { id, audio };
          // Eagerly prefetch the next track in queue after the one we're switching to
          prefetchAfter(id);
        }
        setCurrentId(id);
      }
    },
    [currentId, playing, prefetchAfter]
  );

  const pauseAll = useCallback(() => {
    audioRef.current?.pause();
    setPlaying(false);
    userPausedRef.current = true;
    autoplayPendingRef.current = false;
    clearStallState();
  }, [clearStallState]);


  const seek = useCallback((time: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = Math.max(0, Math.min(time, audio.duration || 0));
    setCurrentTime(audio.currentTime);
  }, []);

  // Coalesce rapid skip presses: extra taps during the skip lock are accumulated
  // here and applied as a single fast-forward of the queue pointer when the lock
  // releases. This prevents 3+ rapid skips from spawning a media element per tap
  // (which exhausts the audio pool, piles up parallel network fetches, and lags
  // the UI). Only ONE media element is created for the final target track.
  const pendingSkipsRef = useRef(0);

  const performSkip = useCallback((extraHops: number) => {
    silenceAll();
    if (pendingAudioRef.current) {
      cleanupMediaElement(pendingAudioRef.current.audio);
      pendingAudioRef.current = null;
    }
    const fromId = currentIdRef.current;
    const allForHistory = flatTracksRef.current();
    // Mark the track we're skipping FROM as played so it cannot recur in this
    // cycle — even if playback never officially "started" (rapid-skip path
    // bypasses the normal recordPlayed() hook that only fires on `playing`).
    if (fromId) {
      appendHistory(fromId, allForHistory);
      playedSessionRef.current.add(fromId);
    }
    let next = advanceToNextRef.current();
    // Fast-forward additional hops without creating any media elements —
    // we only need the queue pointer to land on the final destination track.
    // Each hopped-over track is also recorded so the no-repeat invariant holds.
    for (let i = 0; i < extraHops && next; i++) {
      appendHistory(next.id, allForHistory);
      playedSessionRef.current.add(next.id);
      currentIdRef.current = next.id;
      const further = advanceToNextRef.current();
      if (!further) break;
      next = further;
    }
    if (next) {
      pb.skip("next", fromId, next.id);
      // Start the next track INSIDE this user-gesture stack frame so iOS
      // Safari / Bluetooth sinks treat it as a continuation rather than a
      // fresh autoplay attempt (which adds noticeable warmup latency).
      // Reuse the preloaded element if it happens to match the target.
      let audio: HTMLAudioElement;
      if (preloadRef.current?.id === next.id) {
        audio = preloadRef.current.audio;
        preloadRef.current = null;
      } else {
        audio = createMediaElement(next.src);
        audio.preload = "auto";
      }
      // Compat mode: ramp incoming volume from 0 to mask BT codec overlap.
      rampVolumeIn(audio, COMPAT_FADE_IN_MS);
      audio.play()
        .then(() => { setPlaying(true); userPausedRef.current = false; })
        .catch(() => { /* will be picked up by main effect / interaction unlock */ });
      pendingAudioRef.current = { id: next.id, audio };
      currentIdRef.current = next.id;
      setCurrentId(next.id);
      // Eagerly prefetch the track *after* the one we're skipping to
      prefetchAfter(next.id);
    }
  }, [silenceAll, prefetchAfter]);

  const skipNext = useCallback(() => {
    if (skipLockRef.current) {
      // Coalesce: remember this tap and apply it when the lock releases.
      pendingSkipsRef.current += 1;
      return;
    }
    skipLockRef.current = true;
    performSkip(0);
    setTimeout(() => {
      skipLockRef.current = false;
      const extra = pendingSkipsRef.current;
      if (extra > 0) {
        pendingSkipsRef.current = 0;
        // Re-acquire the lock for the trailing consolidated skip so any
        // further taps during this second window also coalesce cleanly.
        skipLockRef.current = true;
        performSkip(extra);
        setTimeout(() => {
          skipLockRef.current = false;
          // If even more taps arrived during the trailing window, recurse
          // once more via the same mechanism.
          if (pendingSkipsRef.current > 0) {
            const more = pendingSkipsRef.current;
            pendingSkipsRef.current = 0;
            skipLockRef.current = true;
            performSkip(more);
            setTimeout(() => { skipLockRef.current = false; }, 300);
          }
        }, 300);
      }
    }, 300);
  }, [performSkip]);

  const skipPrev = useCallback(() => {
    const audio = audioRef.current;
    if (audio && audio.currentTime > 3) {
      audio.currentTime = 0;
      setCurrentTime(0);
      return;
    }
    silenceAll();
    const queue = queueRef.current;
    const fromId = currentIdRef.current;
    const idx = queue.findIndex((t) => t.id === fromId);
    if (idx > 0) {
      const prevTrack = queue[idx - 1];
      pb.skip("prev", fromId, prevTrack.id);
      setCurrentId(prevTrack.id);
      // Prefetch the track that follows the one we're going back to
      prefetchAfter(prevTrack.id);
    }
  }, [silenceAll, prefetchAfter]);

  const toggleAutoplay = useCallback(() => setAutoplay((v) => !v), []);
  // Shuffle is always forced ON — toggling rebuilds the queue order only
  const toggleShuffle = useCallback(() => setShuffle(true), []);

  // ── Media Session API — lock-screen / notification controls ───────────────
  // Enables background playback continuation on iOS Safari and Android Chrome
  // when the screen locks or app is backgrounded. Without metadata set, mobile
  // browsers will pause audio when the tab loses focus. With metadata set, the
  // OS treats this as a media session and keeps the audio element alive,
  // surfacing controls in the lock screen / notification tray.
  //
  // Limitation: pure web contexts cannot guarantee infinite background play
  // the way a native app can. iOS Safari may suspend after very long lock
  // periods; Android Chrome is more permissive. For installed PWAs the OS
  // treats the session like a music app. No additional native plugin is used.
  useEffect(() => {
    if (typeof navigator === "undefined" || !("mediaSession" in navigator)) return;
    if (!currentTrack) return;

    try {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: currentTrack.title || "Love Language",
        artist: currentTrack.author || "Love Language FM",
        album: currentTrack.dedicateTo
          ? `For ${currentTrack.dedicateTo}`
          : "Sofa silahlane FM",
        // Reuse the site's social card as lock-screen artwork
        artwork: [
          {
            src: "https://storage.googleapis.com/gpt-engineer-file-uploads/Ckt3SasaxnTnb4KWB5nJnhtfL7w2/social-images/social-1772709622912-Malume.webp",
            sizes: "1200x630",
            type: "image/webp",
          },
          { src: "/favicon.ico", sizes: "64x64", type: "image/x-icon" },
        ],
      });
    } catch { /* MediaMetadata not supported — non-fatal */ }
  }, [currentTrack]);

  // Reflect playing state to the OS so lock-screen play/pause icon stays in sync
  useEffect(() => {
    if (typeof navigator === "undefined" || !("mediaSession" in navigator)) return;
    try {
      navigator.mediaSession.playbackState = playing ? "playing" : "paused";
    } catch { /* ignore */ }
  }, [playing]);

  // Wire OS media-control actions to existing public API
  useEffect(() => {
    if (typeof navigator === "undefined" || !("mediaSession" in navigator)) return;
    const ms = navigator.mediaSession;

    const safe = (h: () => void) => () => { try { h(); } catch { /* ignore */ } };

    const handlers: Array<[MediaSessionAction, MediaSessionActionHandler | null]> = [
      ["play", safe(() => {
        const a = audioRef.current;
        if (!a) return;
        userPausedRef.current = false;
        a.play().then(() => setPlaying(true)).catch(() => {});
      })],
      ["pause", safe(() => {
        audioRef.current?.pause();
        setPlaying(false);
        userPausedRef.current = true;
      })],
      ["nexttrack", safe(() => skipNext())],
      ["previoustrack", safe(() => skipPrev())],
      ["seekto", ((details: MediaSessionActionDetails) => {
        if (typeof details.seekTime === "number") seek(details.seekTime);
      }) as MediaSessionActionHandler],
      ["seekbackward", ((details: MediaSessionActionDetails) => {
        const a = audioRef.current; if (!a) return;
        const offset = details.seekOffset ?? 10;
        seek(Math.max(0, a.currentTime - offset));
      }) as MediaSessionActionHandler],
      ["seekforward", ((details: MediaSessionActionDetails) => {
        const a = audioRef.current; if (!a) return;
        const offset = details.seekOffset ?? 10;
        seek(Math.min(a.duration || 0, a.currentTime + offset));
      }) as MediaSessionActionHandler],
    ];

    for (const [action, handler] of handlers) {
      try { ms.setActionHandler(action, handler); } catch { /* unsupported action */ }
    }

    return () => {
      for (const [action] of handlers) {
        try { ms.setActionHandler(action, null); } catch { /* ignore */ }
      }
    };
  }, [skipNext, skipPrev, seek]);

  // Publish position state so the lock-screen scrubber tracks playback
  useEffect(() => {
    if (typeof navigator === "undefined" || !("mediaSession" in navigator)) return;
    if (typeof navigator.mediaSession.setPositionState !== "function") return;
    if (!isFinite(duration) || duration <= 0) return;
    try {
      navigator.mediaSession.setPositionState({
        duration,
        position: Math.min(currentTime, duration),
        playbackRate: 1,
      });
    } catch { /* ignore — invalid position window */ }
  }, [currentTime, duration]);

  // ── Interruption resilience ───────────────────────────────────────────────
  // When a phone call, Bluetooth switch, or headphone disconnect pauses the
  // audio element, sync React state so the UI matches reality. We do NOT
  // auto-resume after a call — the OS owns that decision — but the lock-screen
  // play button (and the in-app play button) will resume cleanly because the
  // audio element is preserved.
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const syncSig = { signal: getSignal(a) };
    a.addEventListener("play", onPlay, syncSig);
    a.addEventListener("pause", onPause, syncSig);
    // Cleanup is handled by AbortSignal on element release
  }, [currentId]);

  return {
    currentId,
    playing,
    
    autoplay,
    shuffle,
    queue: queueRef.current,
    queueVersion,
    currentTrack,
    currentTime,
    duration,
    formatTime,
    playTrack,
    pauseAll,
    seek,
    skipNext,
    skipPrev,
    
    toggleAutoplay,
    toggleShuffle,
  };
}
